import React, { useEffect, useRef, useState } from "react";
import { Mic, Square } from "react-feather";
import { errorMessageToast } from "helpers/common";
import { useLazyGetSpeechToTextTokenQuery } from "store/services/utility";

// Tokens are deliberately kept only in memory and refreshed shortly before expiry.
let cachedToken;
let tokenExpiresAt = 0;
let tokenRequest;

const getToken = async (requestToken) => {
  const now = Math.floor(Date.now() / 1000);
  if (cachedToken && tokenExpiresAt > now + 30) return cachedToken;

  if (!tokenRequest) {
    tokenRequest = requestToken()
      .then((data) => {
        if (!data?.value) throw new Error("Speech-to-text token was not returned by the server.");
        cachedToken = data.value;
        tokenExpiresAt = Number(data.expires_at) || now;
        return cachedToken;
      })
      .finally(() => {
        tokenRequest = null;
      });
  }

  return tokenRequest;
};

// idle: ready to record. connecting: opening the session. listening: audio is being sent.
const STATUS_COLORS = {
  idle: "#93278F",
  connecting: "#E08600",
  listening: "#D32F2F",
};

// Realtime transcription bills on audio duration, and silence costs the same as
// speech, so a session nobody is talking to is stopped instead of left streaming.
const SILENCE_TIMEOUT_MS = 60000;
const MAX_SESSION_MS = 10 * 60 * 1000;
const SOCKET_TIMEOUT_MS = 10000;
const TURN_FLUSH_MS = 2500;
// ~85ms of audio per chunk, so this caps the pre-connection buffer near 10 seconds.
const MAX_PENDING_CHUNKS = 120;

const microphoneErrorMessage = (error) => {
  switch (error?.name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
      return "Microphone access is blocked. Allow the microphone for this site in your browser settings, then try again.";
    case "NotFoundError":
    case "DevicesNotFoundError":
      return "No microphone was found. Connect a microphone and try again.";
    case "NotReadableError":
    case "TrackStartError":
      return "The microphone is already in use by another application.";
    case "OverconstrainedError":
      return "The selected microphone is not available. Try a different input device.";
    case "SecurityError":
      return "Microphone access needs a secure (https) connection.";
    default:
      return error?.message || "The microphone could not be started.";
  }
};

const toBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let index = 0; index < bytes.length; index += 1) binary += String.fromCharCode(bytes[index]);
  return window.btoa(binary);
};

const resampleToPcm16 = (samples, sourceRate, targetRate = 24000) => {
  const sampleCount = Math.max(1, Math.round(samples.length * targetRate / sourceRate));
  const pcm = new Int16Array(sampleCount);

  for (let index = 0; index < sampleCount; index += 1) {
    const sourceIndex = Math.min(samples.length - 1, Math.round(index * sourceRate / targetRate));
    const sample = Math.max(-1, Math.min(1, samples[sourceIndex]));
    pcm[index] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }

  return pcm.buffer;
};

// While speech is continuous the model runs several transcription items at once, so
// partial text has to be keyed by item_id. Completing one item must not wipe the
// deltas already streaming in for the next one, otherwise words get skipped.
const joinLiveItems = (liveByItem) =>
  Array.from(liveByItem.values()).join(" ").replace(/\s+/g, " ").trim();

/**
 * A start/stop microphone control for OpenAI Realtime transcription. Text streams in
 * as it is spoken: `onInterimTranscript` receives the current unfinalised text (and ""
 * once it is folded away) and `onTranscript` receives each finalised segment.
 * `onStart`/`onStop` bracket a session, `onStop` firing after its last segment.
 * Listening stops itself on prolonged silence and at a hard time cap so a session left
 * running by mistake stops billing audio. It never persists the token.
 */
const SpeechToTextButton = ({
  onTranscript,
  onInterimTranscript,
  onStart,
  onStop,
  onError,
  disabled = false,
  language = "en",
  prompt,
  // gpt-realtime-whisper streams deltas continuously via `delay` instead of waiting
  // for a turn to end, which is what makes text appear as it is spoken.
  model = "gpt-realtime-whisper",
  delay = "minimal",
  // Stops itself after this long without any transcript, and at the hard cap, so a
  // session left running by mistake cannot keep billing audio.
  silenceTimeoutMs = SILENCE_TIMEOUT_MS,
  maxSessionMs = MAX_SESSION_MS,
  className = "",
  title = "Start speech-to-text",
}) => {
  const [getSpeechToken] = useLazyGetSpeechToTextTokenQuery();
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [notice, setNotice] = useState("");

  const socketRef = useRef(null);
  const socketPromiseRef = useRef(null);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const processorRef = useRef(null);

  const isListeningRef = useRef(false);
  const hasSentAudioRef = useRef(false);
  const isStoppingRef = useRef(false);
  const isUnmountedRef = useRef(false);
  const pendingAudioRef = useRef([]);
  const liveByItemRef = useRef(new Map());
  const flushedItemsRef = useRef(new Set());
  const flushTimerRef = useRef(null);
  const activityTimerRef = useRef(null);
  const sessionTimerRef = useRef(null);

  // Handlers live on a ref so the socket and window callbacks see the latest props.
  const callbacksRef = useRef({});
  callbacksRef.current = { onTranscript, onInterimTranscript, onStart, onStop, onError };
  // stop() is defined below but the auto-stop timers are scheduled above it.
  const stopRef = useRef(() => {});

  const setSafeStatus = (next) => {
    if (!isUnmountedRef.current) setStatus(next);
  };

  const setSafeNotice = (next) => {
    if (!isUnmountedRef.current) setNotice(next);
  };

  const reportError = (error) => {
    const message = typeof error === "string" ? error : microphoneErrorMessage(error);
    if (!isUnmountedRef.current) setErrorMessage(message);
    if (typeof callbacksRef.current.onError === "function") callbacksRef.current.onError(message);
    else errorMessageToast(message);
  };

  const emitInterim = () => {
    callbacksRef.current.onInterimTranscript?.(joinLiveItems(liveByItemRef.current));
  };

  const emitFinal = (text) => {
    const trimmed = (text || "").trim();
    if (trimmed) callbacksRef.current.onTranscript?.(trimmed);
  };

  // Anything still partial when the turn ends is kept rather than dropped. The ids are
  // remembered so a late `completed` for the same item does not duplicate the text.
  const flushLiveItems = () => {
    if (!liveByItemRef.current.size) return;
    const pending = joinLiveItems(liveByItemRef.current);
    liveByItemRef.current.forEach((value, id) => flushedItemsRef.current.add(id));
    liveByItemRef.current.clear();
    emitInterim();
    emitFinal(pending);
  };

  const endTurn = () => {
    if (flushTimerRef.current) {
      window.clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    isStoppingRef.current = false;
    flushLiveItems();
    teardown();
    callbacksRef.current.onStop?.();
  };

  const releaseMicrophone = () => {
    if (processorRef.current) {
      processorRef.current.onaudioprocess = null;
      processorRef.current.disconnect();
    }
    if (sourceRef.current) sourceRef.current.disconnect();
    if (streamRef.current) streamRef.current.getTracks().forEach((track) => track.stop());
    processorRef.current = null;
    sourceRef.current = null;
    streamRef.current = null;
    pendingAudioRef.current = [];
  };

  const closeSocket = () => {
    const socket = socketRef.current;
    socketRef.current = null;
    if (socket && socket.readyState < WebSocket.CLOSING) socket.close();
  };

  const clearSessionTimers = () => {
    if (activityTimerRef.current) {
      window.clearTimeout(activityTimerRef.current);
      activityTimerRef.current = null;
    }
    if (sessionTimerRef.current) {
      window.clearTimeout(sessionTimerRef.current);
      sessionTimerRef.current = null;
    }
  };

  const teardown = () => {
    clearSessionTimers();
    releaseMicrophone();
    closeSocket();
    flushedItemsRef.current.clear();
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  };

  // Every transcript event restarts the inactivity countdown, so a therapist who
  // pauses to think keeps the session, and an empty room loses it.
  const bumpActivity = () => {
    if (!isListeningRef.current || !silenceTimeoutMs) return;
    if (activityTimerRef.current) window.clearTimeout(activityTimerRef.current);
    activityTimerRef.current = window.setTimeout(() => stopRef.current("silence"), silenceTimeoutMs);
  };

  const handleEvent = (event) => {
    switch (event.type) {
      case "conversation.item.input_audio_transcription.delta": {
        const id = event.item_id || "_";
        if (flushedItemsRef.current.has(id)) break;
        bumpActivity();
        liveByItemRef.current.set(id, (liveByItemRef.current.get(id) || "") + (event.delta || ""));
        emitInterim();
        break;
      }
      case "conversation.item.input_audio_transcription.completed": {
        const id = event.item_id || "_";
        if (flushedItemsRef.current.has(id)) {
          flushedItemsRef.current.delete(id);
          break;
        }
        bumpActivity();
        // Prefer the authoritative final transcript; fall back to the deltas.
        const finalText = event.transcript || liveByItemRef.current.get(id) || "";
        liveByItemRef.current.delete(id); // other items keep streaming
        emitInterim();
        emitFinal(finalText);
        // The turn is fully settled, so release the caller without waiting on the timer.
        if (!isListeningRef.current && !liveByItemRef.current.size && flushTimerRef.current) endTurn();
        break;
      }
      case "error":
        // A commit on an already-drained buffer is expected while finishing a turn.
        if (!isStoppingRef.current) {
          reportError(event.error?.message || "OpenAI transcription error.");
          // Whatever the fault, audio is still being captured and billed against a
          // session that may never transcribe again, so end it rather than stream on.
          stopRef.current("error");
        }
        break;
      default:
        break;
    }
  };

  const ensureSocket = () => {
    if (socketRef.current?.readyState === WebSocket.OPEN) return Promise.resolve(socketRef.current);
    if (socketPromiseRef.current) return socketPromiseRef.current;

    socketPromiseRef.current = (async () => {
      const token = await getToken(() => getSpeechToken().unwrap());
      // OpenAI Realtime accepts browser WebSocket authentication through subprotocols.
      const socket = new WebSocket(
        "wss://api.openai.com/v1/realtime?intent=transcription",
        ["realtime", `openai-insecure-api-key.${token}`]
      );

      try {
        await new Promise((resolve, reject) => {
          const timer = window.setTimeout(
            () => reject(new Error("Timed out connecting to the speech-to-text service.")),
            SOCKET_TIMEOUT_MS
          );
          socket.onopen = () => {
            window.clearTimeout(timer);
            resolve();
          };
          socket.onerror = () => {
            window.clearTimeout(timer);
            reject(new Error("Could not connect to the speech-to-text service."));
          };
          socket.onclose = () => {
            window.clearTimeout(timer);
            reject(new Error("The speech-to-text connection closed before it was ready."));
          };
        });
      } catch (error) {
        // socketRef is only assigned once the handshake succeeds, so this socket is
        // unreachable to closeSocket() and would leak with its ephemeral token.
        if (socket.readyState < WebSocket.CLOSING) socket.close();
        throw error;
      }

      const transcription = {
        model,
        ...(language ? { language } : {}),
        ...(prompt ? { prompt } : {}),
      };
      // The whisper realtime model streams continuously and rejects turn detection;
      // the gpt-4o(-mini)-transcribe models instead segment on server VAD.
      const isWhisper = /whisper/i.test(model);
      if (isWhisper) transcription.delay = delay;

      socket.send(JSON.stringify({
        type: "session.update",
        session: {
          type: "transcription",
          audio: {
            input: {
              format: { type: "audio/pcm", rate: 24000 },
              transcription,
              turn_detection: isWhisper
                ? null
                : { type: "server_vad", threshold: 0.5, silence_duration_ms: 400 },
            },
          },
        },
      }));

      socket.onmessage = ({ data }) => {
        let event;
        try {
          event = JSON.parse(data);
        } catch (parseError) {
          return;
        }
        handleEvent(event);
      };
      socket.onerror = () => {
        if (!isStoppingRef.current) reportError("Lost the connection to the speech-to-text service.");
      };
      socket.onclose = () => {
        // closeSocket() clears the ref before closing, and close() only lands a tick
        // later, so a socket that is no longer the active one was closed deliberately
        // -- possibly by a session we have already replaced. Never blame the new one.
        if (socketRef.current !== socket) return;
        socketRef.current = null;
        // A close we did not ask for: the mic would otherwise keep running and buffer
        // audio nobody is listening to, ending in a misleading "no speech" notice.
        if (isListeningRef.current) {
          reportError("The speech-to-text connection closed unexpectedly. Please start it again.");
          stopRef.current("connection");
        }
      };

      socketRef.current = socket;
      return socket;
    })();

    return socketPromiseRef.current.finally(() => {
      socketPromiseRef.current = null;
    });
  };

  const sendAudio = (socket, chunk) => {
    socket.send(JSON.stringify({ type: "input_audio_buffer.append", audio: chunk }));
    hasSentAudioRef.current = true;
  };

  const ensureMicrophone = async () => {
    if (streamRef.current?.active) return;
    if (!window.isSecureContext) {
      const error = new Error("Microphone access needs a secure (https) connection.");
      error.name = "SecurityError";
      throw error;
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
    });
    streamRef.current = stream;

    const context = audioContextRef.current || new window.AudioContext();
    audioContextRef.current = context;
    if (context.state === "suspended") await context.resume();

    const source = context.createMediaStreamSource(stream);
    const processor = context.createScriptProcessor(4096, 1, 1);
    sourceRef.current = source;
    processorRef.current = processor;

    processor.onaudioprocess = (event) => {
      if (!isListeningRef.current) return;
      const chunk = toBase64(resampleToPcm16(event.inputBuffer.getChannelData(0), context.sampleRate));
      const socket = socketRef.current;

      if (socket?.readyState === WebSocket.OPEN) {
        // Audio captured while the socket was still opening goes out first, so the
        // first words of a cold start are not lost.
        pendingAudioRef.current.forEach((pending) => sendAudio(socket, pending));
        pendingAudioRef.current = [];
        sendAudio(socket, chunk);
      } else if (pendingAudioRef.current.length < MAX_PENDING_CHUNKS) {
        pendingAudioRef.current.push(chunk);
      }
    };

    source.connect(processor);
    processor.connect(context.destination);
  };

  const start = async () => {
    if (disabled || isListeningRef.current) return;
    if (!navigator.mediaDevices?.getUserMedia || !window.WebSocket || !window.AudioContext) {
      reportError("Speech-to-text requires a browser with microphone and WebSocket support.");
      return;
    }

    // A stop() from a moment ago may still be waiting on trailing transcripts. Settle
    // it now, or its timer would fire mid-session and tear down what we open below.
    if (flushTimerRef.current) endTurn();

    isListeningRef.current = true;
    hasSentAudioRef.current = false;
    pendingAudioRef.current = [];
    setErrorMessage("");
    setNotice("");
    setSafeStatus("connecting");
    callbacksRef.current.onStart?.();

    try {
      // Run in parallel: the permission prompt and the handshake overlap on a cold start.
      await Promise.all([ensureMicrophone(), ensureSocket()]);
      if (!isListeningRef.current) {
        // Stopped while connecting: the microphone opened after stop() ran, so it has
        // to be handed back here or the recording indicator would stay lit.
        teardown();
        return;
      }
      setSafeStatus("listening");
      bumpActivity();
      if (maxSessionMs) {
        sessionTimerRef.current = window.setTimeout(() => stopRef.current("limit"), maxSessionMs);
      }
    } catch (error) {
      isListeningRef.current = false;
      setSafeStatus("idle");
      teardown();
      reportError(error);
      callbacksRef.current.onStop?.();
    }
  };

  /** reason: "user" | "silence" | "limit" */
  const stop = (reason = "user") => {
    if (!isListeningRef.current) return;
    isListeningRef.current = false;
    clearSessionTimers();
    setSafeStatus("idle");
    releaseMicrophone();

    if (reason === "silence") {
      setSafeNotice(`Listening stopped after ${Math.round(silenceTimeoutMs / 1000)} seconds without speech. Start it again to carry on.`);
    } else if (reason === "limit") {
      setSafeNotice(`Listening stopped at the ${Math.round(maxSessionMs / 60000)} minute limit. Start it again to carry on.`);
    }

    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN && hasSentAudioRef.current) {
      isStoppingRef.current = true;
      socket.send(JSON.stringify({ type: "input_audio_buffer.commit" }));
      // Trailing completed events arrive after the commit; whatever has not landed by
      // then is folded in so the turn is never lost.
      flushTimerRef.current = window.setTimeout(endTurn, TURN_FLUSH_MS);
    } else {
      endTurn();
    }

    hasSentAudioRef.current = false;
  };
  stopRef.current = stop;

  useEffect(() => () => {
    isUnmountedRef.current = true;
    isListeningRef.current = false;
    if (flushTimerRef.current) window.clearTimeout(flushTimerRef.current);
    teardown();
  }, []);

  const color = STATUS_COLORS[status];
  const isListening = status === "listening";
  const isConnecting = status === "connecting";
  const actionLabel = isListening ? "Stop speech-to-text" : title;

  return (
    <>
      <button
        type="button"
        className={className}
        style={{
          border: "none",
          backgroundColor: "transparent",
          color,
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: 0,
          userSelect: "none",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
        title={isConnecting ? "Connecting…" : actionLabel}
        aria-label={actionLabel}
        aria-pressed={isListening}
        disabled={disabled || isConnecting}
        onClick={() => (isListening ? stop("user") : start())}
      >
        {isListening
          ? <Square color={color} size={14} className="speech-to-text-pulse" />
          : <Mic color={color} size={16} />}
        <span style={{ color }} className="ms-1">
          {isConnecting ? "Connecting" : isListening ? (
            <>Stop <span style={{ color: "black" }}>(Please review and verify accuracy of your text)</span></>
          ) : ""}
        </span>
      </button>
      {errorMessage && <div className="text-danger small mt-1">{errorMessage}</div>}
      {!errorMessage && notice && <div className="text-muted small mt-1">{notice}</div>}
    </>
  );
};

export default SpeechToTextButton;
